import type { ReactElement } from "react"
import TileMol, {
  type LinkTypes,
} from "@onestore/hel/dist/components/molecules/TileMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import GridOrg from "@onestore/hel/dist/components/organisms/GridOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import type {
  Card,
  RecommendedProductSectionFlatData,
} from "@gatsby-plugin-generic-page/fragments/recommendedProductsSection"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import { SquidexComponentType } from "@gatsby-plugin-generic-page/types/componentType"
import { typeMatches } from "@onestore-graphql"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import type { ButtonElement } from "../fragments/buttonElement"
import type { LinkElement } from "../fragments/linkElement"

export default function RecommendedProductSection({
  header,
  cards,
  footer,
  sectionSettings,
}: RecommendedProductSectionFlatData): ReactElement<RecommendedProductSectionFlatData> {
  const { currentPriceType } = usePriceTypeContext()

  const cardsWithImage: Card[] = cards.filter(
    (card) => !isEmpty(card) && !isEmpty(card.images)
  )
  const cardsGridLargeColumns = cardsWithImage.length ? 2 : 3

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {header ? (
          <PushOrg bottomSpace={{ small: 4, medium: 10, large: 10 }}>
            <AlignContentOrg
              horizontalAlign={{
                small: "left",
                medium: "center",
                large: "center",
              }}
            >
              <Header {...header} />
            </AlignContentOrg>
          </PushOrg>
        ) : null}

        <GridOrg
          columns={{ small: 1, medium: 2, large: cardsGridLargeColumns }}
          gutterSpace={{
            small: 4,
            medium: 4,
            large: 10,
          }}
        >
          {cards.map((card, index) => {
            let linkType: SquidexComponentType | undefined
            let linkObject: LinkElement | undefined
            let button:
              | Pick<ButtonElement, "color" | "variant" | "isWider">
              | undefined

            if (
              typeMatches(card.linkAction.__typename, SquidexComponentType.LINK)
            ) {
              linkType = SquidexComponentType.LINK
              linkObject = card.linkAction as LinkElement
            }

            if (
              typeMatches(
                card.linkAction.__typename,
                SquidexComponentType.BUTTON
              )
            ) {
              const { link, color, variant, isWider } =
                card.linkAction as ButtonElement

              linkType = SquidexComponentType.BUTTON
              linkObject = link

              button = {
                color,
                variant,
                isWider,
              }
            }

            const {
              textWithEmbeddedContent,
              title,
              to,
              href,
              openInNewTab,
              onClick,
            } = (linkObject &&
              !isEmpty(linkObject) &&
              getLinkProps(linkObject, currentPriceType)) as ReturnType<
              typeof getLinkProps
            >

            return (
              <TileMol
                key={`${card.title}-${index}`}
                image={
                  card.images
                    ? assetsSquidexToImageObject(card.images, card.title)
                    : undefined
                }
                caption={card.caption}
                title={card.title}
                titleTypography={card.titleTypography?.typography}
                text={
                  card.text ? (
                    <MarkdownText
                      typography={{ bold: "small4", default: "small1" }}
                    >
                      {card.text}
                    </MarkdownText>
                  ) : null
                }
                footerText={
                  <div className="uppercase">
                    <MarkdownText
                      typography={{
                        bold: "small3",
                        default: "small3",
                      }}
                      pushSpace={0}
                      color="brand"
                    >
                      {/* textWithEmbeddedContent can genuinely be undefined
                          (link data may not resolve to a LINK component);
                          MarkdownText's children prop is shared and required,
                          so fall back to an empty string rather than widening
                          that unrelated component's type. */}
                      {textWithEmbeddedContent ?? ""}
                    </MarkdownText>
                  </div>
                }
                linkType={linkType as unknown as LinkTypes}
                to={to}
                href={href}
                hrefTitle={title}
                openInNewTab={openInNewTab}
                button={button}
                hasArrow={card.hasArrow}
                backgroundColor={card.backgroundColor?.background}
                isReversed={card.isReversed}
                onClick={onClick}
              />
            )
          })}
        </GridOrg>

        {footer ? (
          <PushOrg topSpace={6}>
            <AlignContentOrg horizontalAlign="center">
              <MarkdownText>{footer}</MarkdownText>
            </AlignContentOrg>
          </PushOrg>
        ) : null}
      </BaseContainer>
    </BaseSection>
  )
}
